/* eslint-disable no-console */
import { useRuntimeConfig } from "nuxt/app";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { IntercomOptions } from "@/types/IntercomOptions";
import { useFeatureManagement } from "@/composables/useFeatureManagement";

export default defineNuxtRouteMiddleware(() => {
  // Skip middleware on server
  if (import.meta.server) return;

  const config = useRuntimeConfig();
  const intercom = window.Intercom;

  // Only proceed if in production and Intercom is available
  if (!config.public.IS_PROD || typeof intercom !== "function") {
    console.error("Intercom not available to initialize");
    return;
  }

  // Get stores
  const userStore = useUserStore();
  const orgStore = useOrgStore();

  // Check if we have both user and org data - both are required
  const user = userStore.getUser;
  const org = orgStore.getOrg;

  if (!user?.id || !org?.id || !org?.attributes) {
    console.log(
      "Intercom initialization skipped: Required user or org data not available",
    );
    return;
  }

  const attrs = org.attributes;

  // Get license data from FMS instead of legacy org attributes
  const { getDigitalLicenses, getPrintLicenses, getLicenseRegion } =
    useFeatureManagement();
  const digitalLicenses = getDigitalLicenses();
  const printLicenses = getPrintLicenses();
  const licenseRegion = getLicenseRegion();

  const intercomOptions: IntercomOptions = {
    // Base config
    api_base: config.public.INTERCOM_APP_URL as string,
    app_id: config.public.INTERCOM_APP_ID as string,
    hide_default_launcher: true,
    browserLanguage: navigator.language,

    // User data
    name: user.name,
    user_id: `360-${user.id}`,
    user_hash: user.intercom_hash,
    email: user.email,
    userSignUpDate: user.created_at
      ? new Date(user.created_at).toISOString()
      : undefined,

    // Org data
    orgName: attrs.name,
    orgId: org.id,
    master_customer_number: attrs.master_customer_number ?? "1234567890",
    planName: attrs.plan_name,
    orgSignUpDate: attrs.created_at
      ? new Date(attrs.created_at).toISOString()
      : undefined,
    orgEndDate: attrs.end_date
      ? new Date(attrs.end_date).toISOString()
      : undefined,
    trialAccount: attrs.trial_account,
    digitalLicenses,
    printLicenses,
    licenseRegion: licenseRegion || "",

    // Company object
    company: {
      id: attrs.master_customer_number ?? "1234567890",
      name: attrs.name,
      client_org_id: org.id,
      endDate: attrs.end_date
        ? new Date(attrs.end_date).toISOString()
        : undefined,
      trialAccount: attrs.trial_account,
      planName: attrs.plan_name,
      timeZone: attrs.time_zone,
      digitalLicenses,
      printLicenses,
      licenseRegion: licenseRegion || "",
      subscriptionId: org.relationships?.subscriptions?.data?.[0]?.id || null,
      internalAccount: attrs.internal_account || false,
    },
  };

  // Boot Intercom with the set options
  try {
    intercom("boot", intercomOptions);
  } catch (error) {
    console.error("Failed to boot Intercom:", error);
  }
});
